import {computed, ref} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {useTranslation} from '#imports';
import {withCleanup} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {ProjectActivityResource, ProjectPaymentNotificationsResourceNotificationsInner} from '@api';
import {useProjectInformationStore} from '@/features/project-view/stores';
import {projectEndpoints} from '@/registry';

export const useActivityStore = defineStore('activity', () => {
    const {t} = useTranslation();
    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const pendingProjectActivity = ref(false);
    const projectActivity = ref<ProjectActivityResource | null>(null);
    const paymentNotifications = ref<ProjectPaymentNotificationsResourceNotificationsInner[]>([]);

    const pendingActions = computed(() => projectActivity.value?.pendingActions);
    const pastEvents = computed(() => projectActivity.value?.pastEvents);

    const resolveProjectId = (projectId?: string) => projectId ?? projectInformation.value?.projectId;

    async function fetchPaymentNotifications(projectId?: string) {
        const id = resolveProjectId(projectId);
        if (!id) return;

        try {
            const response = await projectEndpoints.getProjectPaymentNotifications({projectId: id});
            paymentNotifications.value = response.notifications;
        } catch (error) {
            pushVJoySnackbar({
                props: {
                    level: 'warning',
                    message: t('projectfreelancer.projectview.notifications.loading.error'),
                },
            });
        }
    }

    async function fetchProjectActivity(projectId?: string) {
        const id = resolveProjectId(projectId);
        if (!id) return;

        pendingProjectActivity.value = true;

        try {
            const [activity] = await Promise.all([projectEndpoints.getProjectActivity({projectId: id}), fetchPaymentNotifications(id)]);

            projectActivity.value = activity;
        } catch (error) {
            pushVJoySnackbar({
                props: {
                    level: 'warning',
                    message: t('projectfreelancer.projectview.activity.loading.error'),
                },
            });
        } finally {
            pendingProjectActivity.value = false;
        }
    }

    return withCleanup({
        fetchProjectActivity,
        fetchPaymentNotifications,
        projectActivity,
        pastEvents,
        pendingActions,
        paymentNotifications,
        pendingProjectActivity,
    });
});
